import { SelfieSegmentation, Results } from '@mediapipe/selfie_segmentation';
import {
    EFFECT_MODE,
    BLUR_AMOUNT,
    CANVAS_SETTINGS,
    MEDIAPIPE_CONFIG,
} from 'flow/constants/video-background-constants';

type EffectMode = (typeof EFFECT_MODE)[keyof typeof EFFECT_MODE];

interface VideoFrameMetadata {
    presentationTime: DOMHighResTimeStamp;
    expectedDisplayTime: DOMHighResTimeStamp;
    width: number;
    height: number;
    mediaTime: number;
    presentedFrames: number;
    processingDuration?: number;
    captureTime?: DOMHighResTimeStamp;
    receiveTime?: DOMHighResTimeStamp;
    rtpTimestamp?: number;
}

type VideoFrameCallback = (
    now: DOMHighResTimeStamp,
    metadata: VideoFrameMetadata,
) => void;

// Extend HTMLVideoElement to support requestVideoFrameCallback API
declare global {
    interface HTMLVideoElement {
        requestVideoFrameCallback(callback: VideoFrameCallback): number;
        cancelVideoFrameCallback(handle: number): void;
    }
}

/**
 * VideoBackgroundProcessor
 *
 * Handles video background effects using MediaPipe Selfie Segmentation.
 * Supports blur and background replacement effects on video streams.
 *
 * Configuration:
 * - VIDEO_BLUR_AMOUNT: Environment variable to set the default blur amount (5-100 pixels)
 *   Example: VIDEO_BLUR_AMOUNT=20 npm start
 *   If not set, defaults to 35 pixels.
 * - VIDEO_RESOLUTION: Environment variable to set camera resolution
 *   Options: 'HD' (1280x720) or default (640x360)
 *   Example: VIDEO_RESOLUTION=HD npm start
 *   If not set, defaults to 640x360.
 * - VIDEO_FRAME_RATE: Environment variable to set the target frame rate (10-60 FPS)
 *   Example: VIDEO_FRAME_RATE=30 npm start
 *   If not set, defaults to 17 FPS.
 */
class VideoBackgroundProcessor {
    private selfieSegmentation: SelfieSegmentation | null = null;
    private canvas: HTMLCanvasElement | null = null;
    private canvasCtx: CanvasRenderingContext2D | null = null;
    private videoElement: HTMLVideoElement | null = null;
    private outputStream: MediaStream | null = null;
    private isProcessing: boolean = false;
    private blurAmount: number;
    private mode: EffectMode = EFFECT_MODE.NONE;
    private backgroundImage: HTMLImageElement | null = null;
    private backgroundImageUrl: string | null = null;
    private videoFrameCallbackId: number | null = null;
    private isInitialized: boolean = false;
    private isInitializing: boolean = false;
    private isProcessingFrame: boolean = false;
    private _imageLoadVersion: number = 0;
    private blurLoggedOnce: boolean = false;

    constructor() {
        // Get blur amount from environment variable or use default
        const envBlurAmount = process.env.VIDEO_BLUR_AMOUNT;
        const defaultBlurAmount = envBlurAmount
            ? parseInt(envBlurAmount, 10)
            : BLUR_AMOUNT.DEFAULT;
        // Ensure the value is within valid range
        this.blurAmount = Math.max(
            BLUR_AMOUNT.MIN,
            Math.min(BLUR_AMOUNT.MAX, defaultBlurAmount),
        );
    }

    /**
     * Initialize MediaPipe Selfie Segmentation
     */
    async initialize(): Promise<void> {
        if (this.isInitialized) {
            return;
        }

        // Prevent concurrent initialization
        if (this.isInitializing) {
            // Wait for existing initialization to complete
            while (this.isInitializing) {
                await new Promise((resolve) => setTimeout(resolve, 50));
            }
            return;
        }

        this.isInitializing = true;

        try {
            this.selfieSegmentation = new SelfieSegmentation({
                locateFile: (file: string) => {
                    return `${MEDIAPIPE_CONFIG.CDN_BASE_URL}${file}`;
                },
            });

            this.selfieSegmentation.setOptions({
                modelSelection: MEDIAPIPE_CONFIG.MODEL_SELECTION,
            });

            this.isInitialized = true;
        } catch (error) {
            console.error('[VideoProcessor] Failed to initialize:', error);
            this.isInitialized = false;
            throw error;
        } finally {
            this.isInitializing = false;
        }
    }

    /**
     * Set blur amount
     * @param amount - Blur amount in pixels (5-100 recommended)
     */
    setBlurAmount(amount: number): void {
        this.blurAmount = Math.max(
            BLUR_AMOUNT.MIN,
            Math.min(BLUR_AMOUNT.MAX, amount),
        );
    }

    /**
     * Set processing mode
     * @param mode - 'none' | 'blur' | 'image'
     */
    setMode(mode: EffectMode): void {
        if (
            [EFFECT_MODE.NONE, EFFECT_MODE.BLUR, EFFECT_MODE.IMAGE].includes(
                mode,
            )
        ) {
            const oldMode = this.mode;
            this.mode = mode;
            // Reset blur logging when changing modes
            if (oldMode !== mode) {
                this.blurLoggedOnce = false;
                console.info(
                    '[VideoProcessor] Mode changed from',
                    oldMode,
                    'to',
                    mode,
                );
            }
        }
    }

    /**
     * Set background image URL
     * @param imageUrl - URL to background image
     * @returns Promise that resolves when image is loaded or cleared
     */
    setBackgroundImage(imageUrl: string | null): Promise<void> {
        if (!imageUrl) {
            // Clean up existing image
            if (this.backgroundImage) {
                // Remove event listeners to prevent errors
                this.backgroundImage.onload = null;
                this.backgroundImage.onerror = null;
                // Don't set src to empty string as it triggers error event
                // Just clear the reference
                this.backgroundImage = null;
            }
            this.backgroundImageUrl = null;
            this._imageLoadVersion++; // Invalidate any pending loads
            return Promise.resolve();
        }

        // Only reload if URL changed
        if (this.backgroundImageUrl === imageUrl && this.backgroundImage) {
            return Promise.resolve();
        }

        // Increment version to invalidate previous loads
        const currentVersion = ++this._imageLoadVersion;

        // Clean up old image before loading new one
        if (this.backgroundImage) {
            this.backgroundImage.onload = null;
            this.backgroundImage.onerror = null;
            this.backgroundImage = null;
        }

        return new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
                // Only set if this is still the current load request
                if (currentVersion === this._imageLoadVersion) {
                    this.backgroundImage = img;
                    this.backgroundImageUrl = imageUrl;
                    resolve();
                } else {
                    // This load was superseded by a newer request
                    console.info(
                        '[VideoProcessor] Discarding stale image load',
                    );
                    resolve();
                }
            };
            img.onerror = (error) => {
                // Only report error if this is still the current load request
                if (currentVersion === this._imageLoadVersion) {
                    console.error(
                        '[VideoProcessor] Failed to load background image:',
                        error,
                    );
                    this.backgroundImage = null;
                    this.backgroundImageUrl = null;
                    reject(new Error('Failed to load background image'));
                } else {
                    // This load was superseded, don't report as error
                    resolve();
                }
            };
            img.src = imageUrl;
        });
    }

    /**
     * Process video frames using requestVideoFrameCallback
     * @param now - Current time
     * @param metadata - Frame metadata
     */
    onVideoFrame: VideoFrameCallback = (_now, _metadata) => {
        if (!this.videoElement || !this.canvasCtx || !this.isProcessing) {
            return;
        }

        // Skip if already processing a frame to prevent MediaPipe overload
        if (this.isProcessingFrame) {
            return;
        }

        if (this.mode !== EFFECT_MODE.NONE && this.selfieSegmentation) {
            this.isProcessingFrame = true;
            this.selfieSegmentation
                .send({
                    image: this.videoElement,
                })
                .catch((error) => {
                    console.error(
                        '[VideoProcessor] MediaPipe processing error:',
                        error,
                    );
                    this.isProcessingFrame = false;
                    this.scheduleNextFrame();
                });
        } else {
            const { width, height } = this.canvas;
            this.canvasCtx.drawImage(this.videoElement, 0, 0, width, height);
            this.scheduleNextFrame();
        }
    };

    /**
     * Schedule the next frame callback
     */
    private scheduleNextFrame(): void {
        if (this.isProcessing && this.videoElement) {
            this.videoFrameCallbackId =
                this.videoElement.requestVideoFrameCallback(this.onVideoFrame);
        }
    }

    /**
     * Render results from MediaPipe segmentation
     * @param results - Segmentation results from MediaPipe
     */
    private renderResults(results: Results): void {
        if (!this.canvasCtx || !this.canvas || !this.isProcessing) {
            this.isProcessingFrame = false;
            return;
        }

        const { image, segmentationMask } = results;

        // Capture current state to prevent mid-execution changes
        const currentMode = this.mode;
        const currentBlurAmount = this.blurAmount;
        const currentBackgroundImage = this.backgroundImage;

        this.canvasCtx.save();
        this.canvasCtx.clearRect(0, 0, this.canvas.width, this.canvas.height);

        if (currentMode === EFFECT_MODE.BLUR) {
            // Draw the segmentation mask to use as clipping region and apply some blur
            this.canvasCtx.filter = 'blur(8px)';
            this.canvasCtx.drawImage(
                segmentationMask,
                0,
                0,
                this.canvas.width,
                this.canvas.height,
            );

            // Use source-in to only keep the person (foreground)
            this.canvasCtx.globalCompositeOperation = 'source-in';
            this.canvasCtx.filter = 'none';
            this.canvasCtx.drawImage(
                image,
                0,
                0,
                this.canvas.width,
                this.canvas.height,
            );

            // Switch to destination-over to draw background behind person
            this.canvasCtx.globalCompositeOperation = 'destination-over';

            // Apply blur filter and draw the background
            this.canvasCtx.filter = `blur(${currentBlurAmount}px)`;
            this.canvasCtx.drawImage(
                image,
                0,
                0,
                this.canvas.width,
                this.canvas.height,
            );

            // Reset filter
            this.canvasCtx.filter = 'none';
        } else if (
            currentMode === EFFECT_MODE.IMAGE &&
            currentBackgroundImage
        ) {
            // Virtual background replacement
            // Draw the segmentation mask to use as clipping region
            this.canvasCtx.filter = 'blur(4px)'; // Slight blur to soften edges
            this.canvasCtx.drawImage(
                segmentationMask,
                0,
                0,
                this.canvas.width,
                this.canvas.height,
            );

            // Use source-in to only keep the person (foreground)
            this.canvasCtx.globalCompositeOperation = 'source-in';
            this.canvasCtx.filter = 'none';
            this.canvasCtx.drawImage(
                image,
                0,
                0,
                this.canvas.width,
                this.canvas.height,
            );

            // Switch to destination-over to draw background image behind person
            this.canvasCtx.globalCompositeOperation = 'destination-over';

            // Draw background image scaled to canvas size
            this.canvasCtx.drawImage(
                currentBackgroundImage,
                0,
                0,
                this.canvas.width,
                this.canvas.height,
            );
        } else {
            // No effect, just draw the original image
            this.canvasCtx.drawImage(
                image,
                0,
                0,
                this.canvas.width,
                this.canvas.height,
            );
        }

        this.canvasCtx.restore();

        // Reset processing flag and schedule next frame
        this.isProcessingFrame = false;
        this.scheduleNextFrame();
    }

    /**
     * Start processing video stream
     * @param videoElement - Video element with source stream
     * @returns Processed output stream
     */
    async startProcessing(
        videoElement: HTMLVideoElement,
    ): Promise<MediaStream> {
        console.info('[VideoProcessor] startProcessing called');

        if (this.isProcessing) {
            if (!this.outputStream) {
                throw new Error('Processing started but output stream is null');
            }
            return this.outputStream;
        }

        this.videoElement = videoElement;

        // Wait for video metadata to be loaded
        if (videoElement.readyState < 2) {
            await new Promise<void>((resolve) => {
                videoElement.addEventListener(
                    'loadedmetadata',
                    () => resolve(),
                    {
                        once: true,
                    },
                );
            });
        }

        // Create canvas for processing
        this.canvas = document.createElement('canvas');
        this.canvas.width = videoElement.videoWidth;
        this.canvas.height = videoElement.videoHeight;
        this.canvasCtx = this.canvas.getContext(
            '2d',
            CANVAS_SETTINGS.CONTEXT_OPTIONS,
        ) as CanvasRenderingContext2D;

        // Initialize MediaPipe if needed
        await this.initialize();

        // Set up MediaPipe callback
        if (!this.selfieSegmentation) {
            throw new Error('MediaPipe initialization failed');
        }
        this.selfieSegmentation.onResults((results: Results) =>
            this.renderResults(results),
        );

        // Create output stream from canvas
        this.outputStream = this.canvas.captureStream(
            CANVAS_SETTINGS.FRAME_RATE,
        );
        this.isProcessing = true;

        // Start video frame callback processing
        this.scheduleNextFrame();

        return this.outputStream;
    }

    /**
     * Stop processing and clean up resources
     */
    async stopProcessing(): Promise<void> {
        // Signal stop first
        this.isProcessing = false;

        // Wait a bit for any in-flight frame processing to complete
        await new Promise((resolve) => setTimeout(resolve, 100));

        if (this.videoFrameCallbackId && this.videoElement) {
            // Note: cancelVideoFrameCallback is not widely supported yet
            // The callback will naturally stop when isProcessing becomes false
            this.videoFrameCallbackId = null;
        }

        if (this.selfieSegmentation) {
            this.selfieSegmentation.close();
            this.selfieSegmentation = null;
        }

        // Stop all tracks in the output stream to prevent memory leaks
        if (this.outputStream) {
            this.outputStream.getTracks().forEach((track) => track.stop());
            this.outputStream = null;
        }

        // Clean up background image to free memory
        if (this.backgroundImage) {
            // Remove event listeners to prevent errors
            this.backgroundImage.onload = null;
            this.backgroundImage.onerror = null;
            this.backgroundImage = null;
            this.backgroundImageUrl = null;
            console.info('[VideoProcessor] Cleaned up background image');
        }

        // Clear canvas to free memory
        if (this.canvas && this.canvasCtx) {
            this.canvasCtx.clearRect(
                0,
                0,
                this.canvas.width,
                this.canvas.height,
            );
        }

        this.canvas = null;
        this.canvasCtx = null;
        this.videoElement = null;
        this.videoFrameCallbackId = null;
        this.isProcessingFrame = false;
        this.isInitialized = false;
        this.isInitializing = false;
        this.blurLoggedOnce = false;
        this._imageLoadVersion++; // Invalidate any pending image loads

        console.info('[VideoProcessor] Cleanup complete');
    }

    /**
     * Get the processed output stream
     * @returns The processed output stream or null
     */
    getProcessedStream(): MediaStream | null {
        return this.outputStream;
    }

    /**
     * Check if processor is currently active
     * @returns Whether the processor is currently active
     */
    isActive(): boolean {
        return this.isProcessing;
    }

    /**
     * Get current mode
     * @returns Current effect mode
     */
    getMode(): EffectMode {
        return this.mode;
    }

    /**
     * Get current blur amount
     * @returns Current blur amount in pixels
     */
    getBlurAmount(): number {
        return this.blurAmount;
    }
}

export default VideoBackgroundProcessor;
